import useLivlyUser from "@/context/UserProvider";
import { useInsuranceQuote } from "@/routes/checklist/insurance-quote";
import { Spinner } from "./Spinner";
import { Outlet } from "react-router";
import {
  AssurantQuote,
  AssurantQuoteV2,
  SelectionMetaData
} from "@/types/Insurance";
import {
  useIs300KInsurancePolicyService,
  useIsInsuranceAssurantV1Service,
  useIsInsuranceV2Service
} from "@/routes/insurance";
import { useInsuranceV2Quote } from "@/routes/checklist/insurance-quote-v2";
import { useState, useEffect } from "react";

export interface InsuranceQuoteContext {
  data: AssurantQuote | AssurantQuoteV2;
  refetch: () => void;
  isError: boolean;
  isAssurantV2: boolean;
  isFetching: boolean;
  selectionMetaData: SelectionMetaData | null;
  updateMetaData: (newMetaData: SelectionMetaData) => void;
  isRequire300kLiability: boolean;
  error: unknown;
}

export default function InsuranceQuoteLayout() {
  const { user } = useLivlyUser();
  const isAssurantV2 = useIsInsuranceV2Service(user);
  const isAssurantV1 = useIsInsuranceAssurantV1Service(user);
  const isRequire300kLiability = isAssurantV2
    ? useIs300KInsurancePolicyService(user)
    : false;
  const [selectionMetaData, setSelectionMetaData] =
    useState<SelectionMetaData | null>(null);
  const { data, isLoading, isError, error, refetch, isFetching } = isAssurantV2
    ? useInsuranceV2Quote(user, selectionMetaData)
    : useInsuranceQuote(user, { enabled: isAssurantV1 });

  useEffect(() => {
    if (isAssurantV2 && data && (data as AssurantQuoteV2).selectionMetaData) {
      setSelectionMetaData({
        ...(data as AssurantQuoteV2).selectionMetaData!,
        uwAnswers: null
      });
    }
  }, [isAssurantV2, data]);

  const updateMetaData = (newMetaData: SelectionMetaData) => {
    setSelectionMetaData(newMetaData);
  };
  // react-query v4 keeps isLoading true forever for a disabled query (no more 'idle' status),
  // so gate on isFetching too or the Marketplace/CustomLink/Proof pages would spin forever.
  if (isLoading && isFetching) {
    return (
      <div className="flex items-center justify-center flex-1 my-56">
        <Spinner color="livly" size="xl" />
      </div>
    );
  }

  const context: InsuranceQuoteContext = {
    data: isAssurantV2 ? (data as AssurantQuoteV2) : (data as AssurantQuote),
    refetch,
    isError,
    error,
    isAssurantV2,
    isFetching,
    selectionMetaData,
    updateMetaData,
    isRequire300kLiability
  };

  return <Outlet context={context} />;
}
